
import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import platformUtils from "./platformUtils";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import facebookVerifiedIcon from "assets/images/icons/socials/facebook-verified.png";
import youtubeVerifiedIcon from "assets/images/icons/socials/youtube-verified.png";
import Tooltip from "@mui/material/Tooltip";

function ProfileCard({ profile, previousData }) {
  const { label, profilePictureUrl, platform, data, verifiedStatus, link } = profile;

  // Validate the platform logic exists
  const platformLogic = platformUtils[platform];
  if (!platformLogic) {
    console.error(`Unsupported platform: ${platform}`);
    return null;
  }

  // Determine verified badge
  const verifiedIcon =
    platform === "Facebook"
      ? facebookVerifiedIcon
      : platform === "YouTube"
        ? youtubeVerifiedIcon
        : null;

  // Determine platform icon
  const renderPlatformIcon = () => {
    if (platform === "Facebook") return <FacebookRoundedIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5, ml: 0.5, color: "#282828", transform: "scale(1.2)", }} />;
    if (platform === "Instagram") return <InstagramIcon fontSize="medium" sx={{ verticalAlign: "middle", mr: 0.5, ml: 0.5, color: "#282828", transform: "scale(1.2)", }} />;
    if (platform === "YouTube") return <YouTubeIcon fontSize="medium" sx={{ verticalAlign: "middle", mr: 0.5, ml: 0.5, color: "#282828", transform: "scale(1.2)", }} />;
    return null;
  };
  const {
    calculateTotalPosts,
    calculateTotalReactions,
    calculateTotalReach,
    calculateTotalVideoViews,
    calculateTotalProfileViews
  } = platformLogic;

  // Validate `data` before using it
  const validData = Array.isArray(data) ? data : [];
  const previousValidData = Array.isArray(previousData?.data) ? previousData.data : [];

  // We assume data is loaded if this component is rendered by the parent
  // Only show loading if strictly needed, but here we treat empty data as "No Data"
  const hasData = validData.length > 0;
  const hasPreviousData = previousValidData.length > 0;

  // Determine the correct metric name and value (followers or subscribers)
  const metricName = platform === "YouTube" ? "Subscribers" : "Followers";
  const currentMetricField = platform === "YouTube" ? "subscribers" : "followers";

  // Determine content label (Posts vs Videos)
  const contentLabel = platform === "YouTube" ? "Videos" : "Posts";

  // Current data calculations
  const maxData = validData.reduce((prev, curr) => (prev.date > curr.date ? prev : curr), validData[0] || {});
  const maxMetricValue = maxData[currentMetricField] || 0;

  const totalPosts = calculateTotalPosts(validData);
  const totalReactions = calculateTotalReactions(validData);
  const totalReach = calculateTotalReach(validData);
  const totalVideoViews = calculateTotalVideoViews(validData);
  const totalProfileViews = calculateTotalProfileViews(validData);

  const engagementRate = maxMetricValue
    ? (totalReactions / maxMetricValue) * 100
    : 0;

  // Previous data calculations
  const previousMaxData = previousValidData.reduce(
    (prev, curr) => (prev.date > curr.date ? prev : curr),
    previousValidData[0] || {}
  );
  const previousMaxMetricValue = previousMaxData[currentMetricField] || 0;

  const previousTotalPosts = calculateTotalPosts(previousValidData);
  const previousTotalReactions = calculateTotalReactions(previousValidData);
  const previousTotalReach = calculateTotalReach(previousValidData);
  const previousTotalVideoViews = calculateTotalVideoViews(previousValidData);
  const previousTotalProfileViews = calculateTotalProfileViews(previousValidData);

  const previousEngagementRate = previousMaxMetricValue
    ? (previousTotalReactions / previousMaxMetricValue) * 100
    : 0;

  // Percentage change calculations
  const metricPercentageChange = previousMaxMetricValue
    ? ((maxMetricValue - previousMaxMetricValue) / previousMaxMetricValue) * 100
    : 0;

  const postsPercentageChange = previousTotalPosts
    ? ((totalPosts - previousTotalPosts) / previousTotalPosts) * 100
    : 0;

  const reactionsPercentageChange = previousTotalReactions
    ? ((totalReactions - previousTotalReactions) / previousTotalReactions) * 100
    : 0;

  const engagementRatePercentageChange = previousEngagementRate > 0
    ? ((engagementRate - previousEngagementRate) / previousEngagementRate) * 100
    : 0;

  const reachPercentageChange = previousTotalReach
    ? ((totalReach - previousTotalReach) / previousTotalReach) * 100
    : 0;

  const videoViewsPercentageChange = previousTotalVideoViews
    ? ((totalVideoViews - previousTotalVideoViews) / previousTotalVideoViews) * 100
    : 0;

  const profileViewsPercentageChange = previousTotalProfileViews
    ? ((totalProfileViews - previousTotalProfileViews) / previousTotalProfileViews) * 100
    : 0;

  // Helper to format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  };

  // Helper to create tooltip content
  const createTooltipContent = (currVal, prevVal, currData, prevData) => {
    const currStart = currData[0]?.date;
    const currEnd = currData[currData.length - 1]?.date;
    const prevStart = prevData[0]?.date;
    const prevEnd = prevData[prevData.length - 1]?.date;

    return (
      <MDBox textAlign="center">
        <MDTypography variant="caption" color="white" fontWeight="bold">
          Current Value: {typeof currVal === 'number' ? currVal.toLocaleString() : currVal}
        </MDTypography>
        <MDTypography variant="caption" color="white" display="block">
          ({formatDate(currStart)} - {formatDate(currEnd)})
        </MDTypography>
        <MDTypography variant="caption" color="white" fontWeight="bold" mt={1} display="block">
          Previous Value: {typeof prevVal === 'number' ? prevVal.toLocaleString() : prevVal}
        </MDTypography>
        <MDTypography variant="caption" color="white" display="block">
          ({formatDate(prevStart)} - {formatDate(prevEnd)})
        </MDTypography>
      </MDBox>
    );
  };

  // Define the cards
  // Define the cards
  const cards = [
    {
      // color: "dark",
      color: "accent",
      icon: "group",
      title: metricName, // Dynamic title for Followers or Subscribers
      count: hasData ? maxMetricValue : "No Data",
      percentage: {
        color: metricPercentageChange >= 0 ? "success" : "error",
        amount: hasPreviousData ? `${metricPercentageChange.toFixed(2)}%` : "",
        label: hasPreviousData ? `from ${previousMaxData.date || "N/A"}` : "No history",
        tooltip: hasData && hasPreviousData ? createTooltipContent(maxMetricValue, previousMaxMetricValue, validData, previousValidData) : null,
      },
      isVisible: true,
      description: `Total number of ${metricName.toLowerCase()} for this profile`,
    },
    {
      // color: "info",
      color: "accent",
      icon: (platform === "YouTube") ? "video_library" : "post_add", // Optional: Change icon too? keeping simple for now
      title: contentLabel,
      count: hasData ? totalPosts : "No Data",
      percentage: {
        color: postsPercentageChange >= 0 ? "success" : "error",
        amount: hasPreviousData ? `${postsPercentageChange.toFixed(2)}%` : "",
        label: hasPreviousData ? `from ${previousMaxData.date || "N/A"}` : "No history",
        tooltip: hasData && hasPreviousData ? createTooltipContent(totalPosts, previousTotalPosts, validData, previousValidData) : null,
      },
      isVisible: true,
      description: `Total number of ${contentLabel.toLowerCase()} published`,
    },
    {
      // color: "primary",
      color: "accent",
      icon: "favorite",
      title: "Reactions",
      count: hasData ? totalReactions : "No Data",
      percentage: {
        color: reactionsPercentageChange >= 0 ? "success" : "error",
        amount: hasPreviousData ? `${reactionsPercentageChange.toFixed(2)}%` : "",
        label: hasPreviousData ? `from ${previousMaxData.date || "N/A"}` : "No history",
        tooltip: hasData && hasPreviousData ? createTooltipContent(totalReactions, previousTotalReactions, validData, previousValidData) : null,
      },
      isVisible: true,
      description: "Total likes, reactions, and other interactions",
    },
    {
      // color: "success",
      color: "accent",
      icon: "thumb_up",
      title: "Engagement",
      count: hasData ? `${engagementRate.toFixed(2)}%` : "No Data",
      percentage: {
        color: engagementRatePercentageChange >= 0 ? "success" : "error",
        amount: hasPreviousData ? `${engagementRatePercentageChange.toFixed(2)}%` : "",
        label: hasPreviousData ? `from ${previousMaxData.date || "N/A"}` : "No history",
        tooltip: hasData && hasPreviousData ? createTooltipContent(`${engagementRate.toFixed(2)}%`, `${previousEngagementRate.toFixed(2)}%`, validData, previousValidData) : null,
      },
      isVisible: true,
      description: "Percentage of followers interacting with your content",
    },
    // New Metrics
    {
      color: "accent",
      icon: "public", // Icon for Reach
      title: "Reach",
      count: hasData ? totalReach : "No Data",
      percentage: {
        color: reachPercentageChange >= 0 ? "success" : "error",
        amount: hasPreviousData ? `${reachPercentageChange.toFixed(2)}%` : "",
        label: hasPreviousData ? `from ${previousMaxData.date || "N/A"}` : "No history",
        tooltip: hasData && hasPreviousData ? createTooltipContent(totalReach, previousTotalReach, validData, previousValidData) : null,
      },
      isVisible: platform === "Facebook" || platform === "Instagram", // Hide for YouTube
      description: "Number of unique accounts that saw your content",
    },
    {
      color: "accent",
      icon: "play_circle_filled", // Icon for Video Views
      title: "Total Views",
      count: hasData ? totalVideoViews : "No Data",
      percentage: {
        color: videoViewsPercentageChange >= 0 ? "success" : "error",
        amount: hasPreviousData ? `${videoViewsPercentageChange.toFixed(2)}%` : "",
        label: hasPreviousData ? `from ${previousMaxData.date || "N/A"}` : "No history",
        tooltip: hasData && hasPreviousData ? createTooltipContent(totalVideoViews, previousTotalVideoViews, validData, previousValidData) : null,
      },
      isVisible: true, // Show for all (if supported)
      description: "Total number of times your videos were viewed",
    },
    {
      color: "accent",
      icon: "visibility", // Icon for Profile Views
      title: "Profile Views",
      count: hasData ? totalProfileViews : "No Data",
      percentage: {
        color: profileViewsPercentageChange >= 0 ? "success" : "error",
        amount: hasPreviousData ? `${profileViewsPercentageChange.toFixed(2)}%` : "",
        label: hasPreviousData ? `from ${previousMaxData.date || "N/A"}` : "No history",
        tooltip: hasData && hasPreviousData ? createTooltipContent(totalProfileViews, previousTotalProfileViews, validData, previousValidData) : null,
      },
      isVisible: platform === "Facebook" || platform === "Instagram", // Hide for YouTube
      description: "Number of times your profile page was visited",
    },
  ];

  return (
    <Card
      sx={{
        mt: 2,
        pt: 1,
        px: 2,
        pb: 2,
        boxShadow: 2,
        backgroundColor: "grey.200",
      }}
    >
      <MDBox display="flex" alignItems="center" mb={3} ml={-1}>
        {/* Avatar with optional verified badge */}
        <Box sx={{ position: "relative", mr: 1 }}>
          <Tooltip title={label} arrow>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar src={profilePictureUrl} alt={label} sx={{ width: 45, height: 45 }} />
            </a>
          </Tooltip>
          {verifiedStatus && verifiedIcon && (
            <Tooltip title="Verified Profile" arrow>
              <Box
                component="img"
                src={verifiedIcon}
                alt="verified"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: -8,
                  width: 12,
                  height: 12,
                }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Platform Icon */}
        <Box sx={{ mr: 1 }}>
          <Tooltip title={platform} arrow>
            {renderPlatformIcon()}
          </Tooltip>
        </Box>

        {/* Profile Name */}
        <MDTypography variant="h6" fontWeight="medium" sx={{ mt: 0.5, textTransform: "uppercase" }}>
          {label}
        </MDTypography>
      </MDBox>


      <MDBox display="flex" flexWrap="wrap" columnGap={2} rowGap={4}>
        {cards.filter(card => card.isVisible).slice(0, 6).map((card, index) => (
          <MDBox
            key={index}
            sx={{
              flex: "1 1 calc(16.66% - 16px)", // Aim for 6 items per row
              minWidth: "160px", // Wrap if smaller than this
            }}
          >
            <ComplexStatisticsCard {...card} />
          </MDBox>
        ))}
      </MDBox>


      {/* </> */}
    </Card>
  );
}

export default ProfileCard;

